<template>
  <section class="page-shell documents-page">
    <div class="page-heading">
      <div>
        <h1>Documentos</h1>
        <p>Drive interno com minutas geradas, versões editáveis, comentários, validações e downloads.</p>
      </div>
      <div class="page-heading-actions">
        <v-btn outlined color="primary" @click="openManualDocumentDialog">
          <v-icon left small>mdi-file-plus-outline</v-icon>
          Criar documento
        </v-btn>
        <v-btn color="primary" :to="{ name: 'document-generator' }">
          <v-icon left small>mdi-creation</v-icon>
          Gerar com IA
        </v-btn>
      </div>
    </div>

    <section class="data-panel">
      <div class="documents-toolbar">
        <div>
          <h2>Biblioteca documental</h2>
          <span>{{ visibleDocuments.length }} documento(s) disponíveis</span>
        </div>
        <div class="documents-toolbar__actions">
          <v-btn-toggle v-model="documentScope" dense mandatory>
            <v-btn value="owned">Meus documentos</v-btn>
            <v-btn value="shared">Compartilhados comigo</v-btn>
          </v-btn-toggle>
          <v-btn outlined color="primary" :loading="loading" @click="loadDocuments">Atualizar</v-btn>
        </div>
      </div>

      <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
      <v-alert v-if="successMessage" type="success" dense text>{{ successMessage }}</v-alert>

      <div v-if="visibleDocuments.length" class="document-card-list">
        <article v-for="document in paginatedDocuments" :key="document.id" class="document-library-card">
          <div class="document-library-card__main">
            <div class="document-library-card__title">
              <span>{{ document.document_type }} · {{ document.destination || 'Uso interno' }}</span>
              <h3>{{ document.title }}</h3>
              <small v-if="isSharedDocument(document)">Compartilhado por {{ shareOwnerLabel(document) }}</small>
            </div>
            <span class="status-pill" :class="`status-pill--${document.status}`">{{ statusLabel(document.status) }}</span>
          </div>

          <div class="document-library-card__details">
            <section>
              <span>Cliente/caso</span>
              <strong>{{ document.client_name || 'Sem cliente informado' }}</strong>
              <p>{{ document.matter_reference || '-' }}</p>
            </section>
            <section>
              <span>Validação</span>
              <div v-if="validationDecision(document)" class="validation-list-summary">
                <strong>{{ validationDecisionLabel(document) }}</strong>
                <small>{{ validationRiskLabel(document) }} · {{ validationFailedCount(document) }} pendência(s)</small>
              </div>
              <p v-else>Sem validação</p>
            </section>
            <section>
              <span>Versão</span>
              <strong>v{{ document.current_version || 1 }}</strong>
            </section>
            <section>
              <span>Atualizado</span>
              <strong>{{ formatDate(document.updated_at) }}</strong>
            </section>
          </div>

          <div class="document-library-card__actions">
            <v-btn small outlined color="primary" class="document-action-button document-action-button--primary" @click="openDocument(document)">
              <v-icon left small>mdi-open-in-new</v-icon>
              Abrir
            </v-btn>
            <v-btn
              outlined
              color="primary"
              small
              class="document-action-button"
              :disabled="!canValidateDocument(document)"
              :loading="validatingDocumentId === document.id"
              @click="requestValidationConfirmation(document)"
            >
              <v-icon left small>mdi-file-search-outline</v-icon>
              Validar
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="document-action-button"
                  :disabled="!canDownloadDocument(document)"
                  :loading="exportLoadingKey === exportKey(document, 'menu')"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left small>mdi-download-outline</v-icon>
                  Exportar
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="option in exportOptions"
                  :key="option.value"
                  :disabled="exportLoadingKey === exportKey(document, option.value)"
                  @click="downloadDocument(document, option.value)"
                >
                  <v-list-item-icon>
                    <v-icon small>{{ option.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn v-if="canShareDocument(document)" small outlined color="primary" class="document-action-button" @click="openShareDialog(document)">
              <v-icon left small>mdi-share-variant-outline</v-icon>
              Compartilhar
            </v-btn>
          </div>
        </article>
      </div>

      <div v-else-if="!loading" class="empty-document-list">Nenhum documento gerado ainda.</div>

      <footer v-if="visibleDocuments.length" class="documents-pagination">
        <span>{{ paginationStart }}-{{ paginationEnd }} de {{ visibleDocuments.length }} documento(s)</span>
        <v-pagination
          v-model="documentPage"
          :length="documentPageCount"
          :total-visible="6"
          color="primary"
        />
      </footer>
    </section>

    <v-dialog v-model="documentDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card v-if="selectedDocument" class="document-workspace">
        <header class="document-workspace-header">
          <div class="document-workspace-title">
            <h2>{{ selectedDocument.title }}</h2>
            <p>{{ selectedDocument.document_type }} · v{{ selectedDocument.current_version || 1 }}</p>
          </div>
          <div class="document-workspace-actions">
            <v-btn small outlined color="primary" class="document-workspace-action" :disabled="!canCommentSelected" :loading="saving" @click="saveDocument">Salvar</v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="document-workspace-action"
              :disabled="!canEditSelected"
              :loading="aiAdjusting"
              @click="openAiAdjustDialog"
            >
              Ajustar com IA
            </v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="document-workspace-action document-workspace-action--primary"
              :disabled="!canEditSelected"
              :loading="selectedDocument && validatingDocumentId === selectedDocument.id"
              @click="requestValidationConfirmation(selectedDocument)"
            >
              Validar
            </v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="document-workspace-action"
              :disabled="!canEditSelected || sendingToReview"
              :loading="sendingToReview"
              @click="openReviewDialog"
            >
              Enviar revisão
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="document-workspace-action"
                  :disabled="!canDownloadDocument(selectedDocument)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left small>mdi-download-outline</v-icon>
                  Exportar
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="option in exportOptions"
                  :key="option.value"
                  :disabled="exportLoadingKey === exportKey(selectedDocument, option.value)"
                  @click="downloadDocument(selectedDocument, option.value)"
                >
                  <v-list-item-icon>
                    <v-icon small>{{ option.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn v-if="canShareSelected" small outlined color="primary" class="document-workspace-action" @click="openShareDialog(selectedDocument)">
              <v-icon left small>mdi-share-variant-outline</v-icon>
              Compartilhar
            </v-btn>
            <v-btn small icon color="primary" class="document-workspace-close" title="Fechar" @click="documentDialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </header>

        <div v-if="error || successMessage" class="document-workspace-feedback">
          <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
          <v-alert v-if="successMessage" type="success" dense text>{{ successMessage }}</v-alert>
        </div>

        <div class="document-workspace-body">
          <aside class="document-meta-panel">
            <h3>Contexto</h3>
            <label>Título</label>
            <v-text-field v-model="selectedDocument.title" :disabled="!canEditSelected" outlined dense hide-details />
            <label>Cliente</label>
            <v-text-field v-model="selectedDocument.client_name" :disabled="!canEditSelected" outlined dense hide-details />
            <label>Caso/processo</label>
            <v-text-field v-model="selectedDocument.matter_reference" :disabled="!canEditSelected" outlined dense hide-details />
            <label>Status do documento</label>
            <span class="status-pill" :class="`status-pill--${selectedDocument.status}`">{{ statusLabel(selectedDocument.status) }}</span>
            <div v-if="isSharedDocument(selectedDocument)" class="shared-document-note">
              Documento compartilhado por {{ shareOwnerLabel(selectedDocument) }}.
            </div>

            <section class="document-review-history document-side-section document-side-section--human">
              <div class="document-review-history__header">
                <div class="document-side-section__title">
                  <span class="document-side-section__icon">
                    <v-icon small>mdi-account-check-outline</v-icon>
                  </span>
                  <div>
                    <span>Revisão humana</span>
                    <h3>Decisões e histórico</h3>
                  </div>
                </div>
                <strong>{{ selectedDocumentReviews.length }}</strong>
              </div>
              <p class="document-side-section__description">
                Registro das decisões feitas por uma pessoa responsável antes de liberar ou bloquear o documento.
              </p>
              <div v-if="selectedDocumentReviews.length" class="document-review-history__list">
                <article v-for="review in selectedDocumentReviews" :key="review.id" class="document-review-history__item">
                  <div class="document-review-history__item-top">
                    <span class="status-pill" :class="`status-pill--${review.status}`">{{ reviewStatusLabel(review.status) }}</span>
                    <small>{{ reviewDecisionDate(review) }}</small>
                  </div>
                  <div class="document-review-history__actor">
                    <span>Responsável</span>
                    <strong>{{ reviewDecisionActor(review) }}</strong>
                  </div>
                  <div class="document-review-history__reason">
                    <span>Motivo</span>
                    <p>{{ reviewDecisionReason(review) }}</p>
                  </div>
                </article>
              </div>
              <p v-else class="document-review-history__empty">Nenhuma decisão humana registrada para este documento.</p>
            </section>

            <section v-if="selectedValidation" class="document-validation-card document-side-section document-side-section--ai">
              <div class="document-validation-card__header">
                <div class="document-side-section__title">
                  <span class="document-side-section__icon">
                    <v-icon small>mdi-robot-outline</v-icon>
                  </span>
                  <div>
                    <span>Validação por IA</span>
                    <h3>Resultado automático</h3>
                  </div>
                </div>
                <span class="validation-decision-pill" :class="`validation-decision-pill--${selectedValidation.decision.status}`">
                  {{ validationDecisionDisplayLabel(selectedValidation.decision) }}
                </span>
              </div>
              <p class="document-side-section__description">
                Checagem automática de risco, pendências e consistência. Não substitui a revisão humana quando houver decisão de negócio ou saída externa.
              </p>
              <p class="validation-message">{{ selectedValidation.decision.message }}</p>
              <small class="validation-next-step">{{ selectedValidation.decision.next_step }}</small>
              <div class="validation-mini-grid">
                <article>
                  <span>Risco</span>
                  <strong>{{ riskLabel(selectedValidation.decision.risk_level) }}</strong>
                </article>
                <article>
                  <span>Pendências</span>
                  <strong>{{ selectedValidation.decision.failed_count || 0 }}</strong>
                </article>
                <article>
                  <span>Palavras</span>
                  <strong>{{ selectedValidation.facts && selectedValidation.facts.word_count ? selectedValidation.facts.word_count : 0 }}</strong>
                </article>
              </div>
              <div v-if="selectedAiSummary" class="validation-ai-summary">
                <strong>Análise automatizada</strong>
                <p>{{ selectedAiSummary }}</p>
              </div>
              <div v-if="reviewFocusItems.length" class="validation-adjustment-list">
                <strong>Pontos de ajuste</strong>
                <button
                  v-for="item in reviewFocusItems"
                  :key="item.id"
                  type="button"
                  :class="{ active: activeReviewMarkerId === item.id }"
                  @click="focusReviewMarker(item)"
                >
                  <span>{{ item.label }}</span>
                  <p>{{ item.preview }}</p>
                </button>
              </div>
              <div v-if="selectedPendingChecks.length" class="validation-pending-checks">
                <strong>Pendências</strong>
                <ul>
                  <li v-for="check in selectedPendingChecks" :key="check.name">
                    {{ check.name }}
                  </li>
                </ul>
              </div>
            </section>
          </aside>

          <main class="document-edit-viewer">
            <document-editor-workspace
              ref="documentEditor"
              v-model="selectedDocument.document_text"
              :comments="parsedComments"
              :layout-settings="selectedDocument.layout_settings"
              :content-html="selectedValidation ? highlightDocumentText(selectedDocument.document_text) : ''"
              :review-markers="reviewMarkers"
              :readonly="!canEditSelected"
              :can-comment="canCommentSelected"
              :cover-title="selectedDocument.title"
              title="Preview editável"
              :helper="editorPermissionHelper"
              @update:comments="selectedDocument.comments = $event"
              @update:layout-settings="selectedDocument.layout_settings = $event"
            />
          </main>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reviewDialog" max-width="760">
      <v-card class="document-review-dialog">
        <v-card-title>
          <div>
            <span>Revisão humana</span>
            <strong>Enviar documento para revisão</strong>
          </div>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="reviewError" type="error" dense text>{{ reviewError }}</v-alert>

          <section v-if="selectedDocument" class="review-document-summary">
            <div>
              <span>Documento</span>
              <strong>{{ selectedDocument.title }}</strong>
            </div>
            <div>
              <span>Tipo</span>
              <strong>{{ selectedDocument.document_type || 'Documento' }}</strong>
            </div>
            <div>
              <span>Destino</span>
              <strong>{{ selectedDocument.destination || 'Uso interno' }}</strong>
            </div>
          </section>

          <div class="review-mode-grid">
            <button
              v-for="option in reviewRiskOptions"
              :key="option.value"
              type="button"
              :class="{ active: reviewForm.quick_risk === option.value }"
              @click="applyReviewRisk(option.value)"
            >
              <v-icon>{{ option.icon }}</v-icon>
              <strong>{{ option.text }}</strong>
              <span>{{ option.description }}</span>
            </button>
          </div>

          <v-textarea
            v-model="reviewForm.business_purpose"
            label="O que o revisor precisa decidir"
            outlined
            dense
            rows="3"
            hide-details
          />

          <div class="review-dialog-grid">
            <v-select
              v-model="reviewForm.confidentiality_level"
              :items="reviewConfidentialityOptions"
              label="Confidencialidade"
              outlined
              dense
              hide-details
            />
            <v-select
              v-model="reviewForm.human_reviewer"
              :items="reviewerOptions"
              :loading="reviewersLoading"
              label="Responsável"
              outlined
              dense
              hide-details
              no-data-text="Nenhum funcionário ativo encontrado"
            />
          </div>

          <v-textarea
            v-model="reviewForm.controls"
            label="Checklist para a revisão"
            outlined
            dense
            rows="3"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reviewDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="reviewSubmitting" @click="submitDocumentReview">
            Enviar para fila
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="manualDocumentDialog" max-width="720">
      <v-card class="manual-document-dialog">
        <v-card-title>Criar documento</v-card-title>
        <v-card-text>
          <v-alert v-if="manualDocumentError" type="error" dense text>{{ manualDocumentError }}</v-alert>
          <div class="manual-document-grid">
            <v-text-field v-model="manualDocumentForm.title" label="Título" outlined dense hide-details />
            <v-select v-model="manualDocumentForm.document_type" :items="manualDocumentTypes" label="Tipo" outlined dense hide-details />
            <v-text-field v-model="manualDocumentForm.client_name" label="Cliente" outlined dense hide-details />
            <v-text-field v-model="manualDocumentForm.matter_reference" label="Caso/processo/contrato" outlined dense hide-details />
            <v-select v-model="manualDocumentForm.destination" :items="manualDestinations" label="Destino" outlined dense hide-details />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="manualDocumentDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="manualDocumentSaving" @click="createManualDocument">
            Criar e abrir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="aiAdjustDialog" max-width="620">
      <v-card class="ai-adjust-dialog">
        <v-card-title>Ajustar texto com IA</v-card-title>
        <v-card-text>
          <v-alert v-if="aiAdjustError" type="error" dense text>{{ aiAdjustError }}</v-alert>
          <v-alert v-if="aiAdjustSummary" type="success" dense text>{{ aiAdjustSummary }}</v-alert>
          <div class="ai-adjust-mode-grid">
            <button
              v-for="mode in aiAdjustModes"
              :key="mode.value"
              type="button"
              :class="{ active: aiAdjustForm.mode === mode.value }"
              @click="aiAdjustForm.mode = mode.value"
            >
              <v-icon small>{{ mode.icon }}</v-icon>
              <strong>{{ mode.text }}</strong>
              <span>{{ mode.description }}</span>
            </button>
          </div>
          <v-textarea
            v-model="aiAdjustForm.instruction"
            label="Orientação opcional"
            outlined
            rows="4"
            no-resize
            hide-details
          />
          <div class="ai-adjust-credit-preview">
            <span>Estimativa</span>
            <strong>{{ selectedAiAdjustmentCredits }} {{ selectedAiAdjustmentCredits === 1 ? 'crédito' : 'créditos' }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="aiAdjustDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="aiAdjusting" @click="adjustSelectedDocumentWithAi">
            Aplicar ajuste
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="shareDialog" max-width="720">
      <v-card class="document-share-dialog">
        <v-card-title>Compartilhar documento</v-card-title>
        <v-card-text>
          <v-alert v-if="shareError" type="error" dense text>{{ shareError }}</v-alert>
          <v-alert v-if="shareInviteUrl" type="info" dense text>
            Convite criado. Link: {{ shareInviteUrl }}
          </v-alert>
          <div v-if="shareSummary" class="share-limit-summary">
            {{ shareSummary.used }} de {{ shareSummary.limit }} documentos compartilhados no plano.
          </div>
          <div class="share-form-grid">
            <v-text-field v-model="shareForm.email" label="E-mail" outlined dense hide-details />
            <v-select v-model="shareForm.permission" :items="sharePermissionOptions" label="Permissão" outlined dense hide-details />
            <v-btn color="primary" :loading="shareLoading" @click="createShare">Compartilhar</v-btn>
          </div>
          <div class="share-list">
            <article v-for="share in documentShares" :key="share.id">
              <div>
                <strong>{{ share.invited_email }}</strong>
                <span>{{ permissionLabel(share.permission) }} · {{ share.status === 'accepted' ? 'Aceito' : 'Pendente' }}</span>
              </div>
              <v-btn small text color="error" @click="revokeShare(share)">Revogar</v-btn>
            </article>
            <p v-if="!documentShares.length">Nenhum compartilhamento ativo.</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="shareDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="validationConfirmDialog" max-width="460">
      <v-card class="validation-confirm-dialog">
        <v-card-title>Validar com IA?</v-card-title>
        <v-card-text>
          <p>
            Essa validação usa IA e pode consumir créditos do plano.
          </p>
          <div v-if="pendingValidationSnapshot" class="validation-existing-preview">
            <div class="validation-existing-preview__header">
              <div>
                <span>Última validação por IA</span>
                <strong>{{ pendingValidationDate }}</strong>
              </div>
              <span class="validation-decision-pill" :class="`validation-decision-pill--${pendingValidationDecision.status}`">
                {{ validationDecisionDisplayLabel(pendingValidationDecision) }}
              </span>
            </div>
            <p>{{ pendingValidationDecision.message || 'Validação anterior disponível para consulta.' }}</p>
            <button type="button" @click="openPendingValidationDetails">
              Ver detalhes da validação
            </button>
          </div>
          <div class="validation-credit-preview">
            <span>Estimativa</span>
            <strong>{{ pendingValidationCredits }} {{ pendingValidationCredits === 1 ? 'crédito' : 'créditos' }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="validationConfirmDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="validatingDocumentId === pendingValidationDocumentId" @click="confirmValidation">
            Validar agora
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import { billingErrorMessage } from '../../utils/apiErrors'
import DocumentEditorWorkspace from '../../components/DocumentEditorWorkspace.vue'

export default {
  name: 'DocumentsPage',
  components: {
    DocumentEditorWorkspace
  },
  data () {
    return {
      documents: [],
      sharedDocuments: [],
      documentReviews: [],
      documentScope: 'owned',
      documentPage: 1,
      documentsPerPage: 6,
      selectedDocument: null,
      documentDialog: false,
      loading: false,
      saving: false,
      sendingToReview: false,
      reviewDialog: false,
      reviewSubmitting: false,
      reviewError: '',
      reviewersLoading: false,
      employees: [],
      reviewForm: this.emptyReviewForm(),
      reviewRiskOptions: [
        { value: 'internal', icon: 'mdi-office-building-outline', text: 'Uso interno', description: 'Revisar consistência, clareza e aderência a políticas internas.' },
        { value: 'client_data', icon: 'mdi-account-lock-outline', text: 'Cliente/caso', description: 'Conferir dados de cliente, sigilo, valores e estratégia.' },
        { value: 'external', icon: 'mdi-send-check-outline', text: 'Saída externa', description: 'Validar antes de cliente, contraparte, assinatura ou protocolo.' }
      ],
      reviewConfidentialityOptions: [
        { text: 'Público', value: 'public' },
        { text: 'Interno', value: 'internal' },
        { text: 'Confidencial', value: 'confidential' },
        { text: 'Restrito', value: 'restricted' }
      ],
      error: '',
      successMessage: '',
      validatingDocumentId: null,
      exportLoadingKey: '',
      activeReviewMarkerId: '',
      shareDialog: false,
      shareLoading: false,
      shareError: '',
      shareInviteUrl: '',
      shareTargetDocument: null,
      documentShares: [],
      shareSummary: null,
      shareForm: {
        email: '',
        permission: 'view'
      },
      validationConfirmDialog: false,
      pendingValidationDocument: null,
      manualDocumentDialog: false,
      manualDocumentSaving: false,
      manualDocumentError: '',
      manualDocumentForm: this.emptyManualDocumentForm(),
      manualDocumentTypes: ['Minuta', 'Petição', 'Minuta contratual', 'Notificação extrajudicial', 'Parecer interno', 'E-mail para cliente', 'Cláusula contratual', 'Resumo jurídico', 'Documento'],
      manualDestinations: ['Uso interno', 'Enviar para cliente', 'Enviar para contraparte', 'Protocolar em processo', 'Assinatura'],
      aiAdjustDialog: false,
      aiAdjusting: false,
      aiAdjustError: '',
      aiAdjustSummary: '',
      aiAdjustForm: {
        mode: 'improve',
        instruction: ''
      },
      aiAdjustModes: [
        { value: 'improve', text: 'Melhorar', icon: 'mdi-auto-fix', description: 'Clareza, fluidez e organização.' },
        { value: 'formalize', text: 'Formalizar', icon: 'mdi-briefcase-edit-outline', description: 'Tom mais técnico e profissional.' },
        { value: 'simplify', text: 'Simplificar', icon: 'mdi-text-short', description: 'Texto mais direto e fácil de ler.' }
      ],
      sharePermissionOptions: [
        { text: 'Visualizar', value: 'view' },
        { text: 'Comentar', value: 'comment' },
        { text: 'Editar', value: 'edit' }
      ],
      exportOptions: [
        { text: 'PDF', value: 'pdf', icon: 'mdi-file-pdf-box' },
        { text: 'Word (.docx)', value: 'docx', icon: 'mdi-file-word-box' },
        { text: 'Texto (.txt)', value: 'txt', icon: 'mdi-file-document-outline' }
      ]
    }
  },
  computed: {
    parsedComments () {
      if (!this.selectedDocument) return []
      const comments = this.selectedDocument.comments
      if (Array.isArray(comments)) return comments
      if (!comments) return []
      try {
        return JSON.parse(comments) || []
      } catch (error) {
        return []
      }
    },
    selectedValidation () {
      const snapshot = this.validationSnapshot(this.selectedDocument)
      return snapshot && snapshot.decision ? snapshot : null
    },
    selectedAiSummary () {
      if (!this.selectedValidation || !this.selectedValidation.ai_review) return ''
      const aiReview = this.selectedValidation.ai_review
      if (aiReview.review && aiReview.review.executive_summary) return aiReview.review.executive_summary
      return aiReview.summary || ''
    },
    aiAdjustmentPoints () {
      if (!this.selectedValidation || !this.selectedValidation.ai_review) return []
      const review = this.selectedValidation.ai_review.review || null
      if (!review || !Array.isArray(review.adjustment_points)) return []
      return review.adjustment_points.filter(item => item && (item.excerpt || item.issue)).slice(0, 8)
    },
    selectedPendingChecks () {
      if (!this.selectedValidation || !Array.isArray(this.selectedValidation.checks)) return []
      return this.selectedValidation.checks.filter(check => check.status !== 'passed').slice(0, 4)
    },
    reviewMarkers () {
      if (!this.selectedValidation) return []
      const text = this.selectedDocument ? this.selectedDocument.document_text : ''
      const markers = [
        { type: 'numeric', label: 'Valores/períodos', active: this.hasReviewMarker(text, 'numeric') },
        { type: 'placeholder', label: 'Campos incompletos', active: this.hasReviewMarker(text, 'placeholder') },
        { type: 'legal', label: 'Fonte jurídica', active: this.hasReviewMarker(text, 'legal') },
        { type: 'sensitive', label: 'Dados sensíveis', active: this.hasReviewMarker(text, 'sensitive') }
      ]
      return markers.filter(marker => marker.active)
    },
    reviewFocusItems () {
      if (!this.selectedValidation || !this.selectedDocument) return []
      return this.reviewHighlightBlocks(this.selectedDocument.document_text).map(item => ({
        id: item.id,
        type: item.marker.type,
        label: item.marker.label,
        preview: item.marker.issue || this.truncateReviewPreview(item.text)
      }))
    },
    selectedDocumentReviews () {
      if (!this.selectedDocument) return []
      const documentId = Number(this.selectedDocument.id)

      return this.documentReviews
        .filter(review => Number(review.generated_document_id) === documentId)
        .sort((a, b) => new Date(b.completed_at || b.updated_at || b.created_at || 0) - new Date(a.completed_at || a.updated_at || a.created_at || 0))
    },
    selectedWordCount () {
      const text = this.selectedDocument ? this.selectedDocument.document_text : ''
      return String(text || '').trim().split(/\s+/).filter(Boolean).length
    },
    visibleDocuments () {
      return this.documentScope === 'shared' ? this.sharedDocuments : this.documents
    },
    documentPageCount () {
      return Math.max(1, Math.ceil(this.visibleDocuments.length / this.documentsPerPage))
    },
    paginatedDocuments () {
      const start = (this.documentPage - 1) * this.documentsPerPage
      return this.visibleDocuments.slice(start, start + this.documentsPerPage)
    },
    paginationStart () {
      if (!this.visibleDocuments.length) return 0
      return ((this.documentPage - 1) * this.documentsPerPage) + 1
    },
    paginationEnd () {
      return Math.min(this.documentPage * this.documentsPerPage, this.visibleDocuments.length)
    },
    selectedAccess () {
      return this.selectedDocument && this.selectedDocument.access ? this.selectedDocument.access : { role: 'owner', permission: 'owner' }
    },
    canEditSelected () {
      return this.selectedAccess.role === 'owner' || this.selectedAccess.permission === 'edit'
    },
    canCommentSelected () {
      return this.canEditSelected || this.selectedAccess.permission === 'comment'
    },
    canShareSelected () {
      return this.selectedAccess.role === 'owner'
    },
    editorPermissionHelper () {
      if (this.canEditSelected) return 'Edite texto, fonte, estilos, comentários e layout do documento.'
      if (this.canCommentSelected) return 'Você pode visualizar e comentar este documento compartilhado.'
      return 'Você pode visualizar este documento compartilhado.'
    },
    pendingValidationDocumentId () {
      return this.pendingValidationDocument ? this.pendingValidationDocument.id : null
    },
    pendingValidationCredits () {
      return this.estimateValidationCredits(this.pendingValidationDocument)
    },
    pendingValidationSnapshot () {
      return this.validationSnapshot(this.pendingValidationDocument)
    },
    pendingValidationDecision () {
      const snapshot = this.pendingValidationSnapshot
      return snapshot && snapshot.decision ? snapshot.decision : { status: 'unknown', label: 'Validação registrada' }
    },
    pendingValidationDate () {
      const snapshot = this.pendingValidationSnapshot
      if (!snapshot) return '-'
      const evidence = snapshot.evidence || {}
      return this.formatDate(evidence.created_at || evidence.updated_at || snapshot.created_at || snapshot.updated_at)
    },
    selectedAiAdjustmentCredits () {
      if (!this.selectedDocument) return 1
      return this.estimateTextCredits(`${this.selectedDocument.document_text || ''} ${this.aiAdjustForm.instruction || ''}`)
    },
    reviewerOptions () {
      return this.employees
        .filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
        .map(employee => ({
          text: `${employee.name || employee.email} · ${employee.role_name || 'Sem papel'}`,
          value: this.reviewerValue(employee)
        }))
    }
  },
  created () {
    this.loadDocuments()
  },
  watch: {
    documentScope () {
      this.documentPage = 1
    },
    '$route.query.id': {
      immediate: false,
      handler (id) {
        if (id) this.openDocument({ id })
      }
    },
    '$route.query.create': {
      immediate: false,
      handler (value) {
        if (value === 'manual') this.openManualDocumentDialog()
      }
    }
  },
  methods: {
    emptyReviewForm () {
      return {
        quick_risk: 'client_data',
        risk_level: 'medium',
        confidentiality_level: 'confidential',
        uses_personal_data: true,
        business_purpose: '',
        data_categories: 'Dados, sigilo e riscos jurídicos serão avaliados pelo responsável humano.',
        expected_benefit: 'Liberar, ajustar ou bloquear documento antes do uso.',
        output_review_required: true,
        human_reviewer: '',
        controls: 'Revisar conteúdo, fontes, placeholders, dados sensíveis, estratégia e comentários antes de liberar.',
        lastGeneratedRisk: '',
        lastGeneratedBusinessPurpose: '',
        lastGeneratedControls: ''
      }
    },
    emptyManualDocumentForm () {
      return {
        title: '',
        document_type: 'Documento',
        client_name: '',
        matter_reference: '',
        destination: 'Uso interno',
        document_text: ''
      }
    },
    openManualDocumentDialog () {
      this.manualDocumentForm = this.emptyManualDocumentForm()
      this.manualDocumentError = ''
      this.manualDocumentDialog = true
    },
    async createManualDocument () {
      this.manualDocumentError = ''
      const title = String(this.manualDocumentForm.title || '').trim()

      if (!title || !this.manualDocumentForm.document_type) {
        this.manualDocumentError = 'Informe título e tipo do documento.'
        return
      }

      this.manualDocumentSaving = true
      try {
        const layout = this.defaultLayoutSettings()
        layout.cover.title = title
        layout.cover.client_name = this.manualDocumentForm.client_name || ''
        layout.cover.matter_reference = this.manualDocumentForm.matter_reference || ''

        const { data } = await api.post('/generated-documents', {
          ...this.manualDocumentForm,
          title,
          document_text: '',
          status: 'draft',
          comments: [],
          layout_settings: layout
        })

        const document = this.normalizeDocument(data)
        this.documents = [document, ...this.documents.filter(item => item.id !== document.id)]
        this.documentScope = 'owned'
        this.manualDocumentDialog = false
        this.successMessage = 'Documento criado sem IA.'
        await this.openDocument(document)
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : null
        this.manualDocumentError = data ? data.message : 'Não foi possível criar o documento.'
      } finally {
        this.manualDocumentSaving = false
      }
    },
    async loadDocuments () {
      this.loading = true
      this.error = ''
      this.successMessage = ''
      try {
        if (this.$route.query.share_token) {
          await this.acceptShareToken(this.$route.query.share_token)
        }
        const [{ data }, { data: sharedData }] = await Promise.all([
          api.get('/generated-documents'),
          api.get('/generated-documents/shared-with-me')
        ])
        this.documents = data.map(item => this.normalizeDocument(item))
        this.sharedDocuments = sharedData.map(item => this.normalizeDocument(item))
        await this.loadDocumentReviews()
        this.documentPage = Math.min(this.documentPage, this.documentPageCount)
        if (this.$route.query.id && !this.documentDialog) {
          await this.openDocument({ id: this.$route.query.id })
        } else if (this.$route.query.create === 'manual' && !this.manualDocumentDialog) {
          this.openManualDocumentDialog()
        }
      } catch (error) {
        this.error = billingErrorMessage(error, 'Não foi possível carregar os documentos.')
      } finally {
        this.loading = false
      }
    },
    async openDocument (document) {
      this.error = ''
      this.successMessage = ''
      const { data } = await api.get(`/generated-documents/${document.id}`)
      this.selectedDocument = this.normalizeDocument(data)
      this.documentDialog = true
      this.activeReviewMarkerId = ''
      this.$nextTick(() => {
        this.renderEditorText()
      })
    },
    async loadDocumentReviews () {
      try {
        const { data } = await api.get('/human-review-requests')
        this.documentReviews = this.responseRows(data)
      } catch (error) {
        this.documentReviews = []
      }
    },
    responseRows (data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.rows)) return data.rows
      if (data && Array.isArray(data.data)) return data.data
      return []
    },
    renderEditorText () {
      if (!this.$refs.documentEditor || !this.selectedDocument) return
      this.$refs.documentEditor.renderEditorText()
      if (this.activeReviewMarkerId) {
        this.$nextTick(() => this.focusReviewMarker({ id: this.activeReviewMarkerId }, false))
      }
    },
    syncEditorText () {
      if (!this.$refs.documentEditor || !this.selectedDocument) return
      this.$refs.documentEditor.syncEditorText()
    },
    focusReviewMarker (item, scroll = true) {
      if (!item || !item.id) return
      this.activeReviewMarkerId = item.id
      if (this.$refs.documentEditor) this.$refs.documentEditor.focusReviewMarker(item, scroll)
    },
    async saveDocument () {
      if (!this.selectedDocument) return
      this.syncEditorText()
      this.saving = true
      this.error = ''
      try {
        const payload = this.canEditSelected
          ? {
              title: this.selectedDocument.title,
              document_type: this.selectedDocument.document_type,
              status: this.selectedDocument.status,
              client_name: this.selectedDocument.client_name,
              matter_reference: this.selectedDocument.matter_reference,
              destination: this.selectedDocument.destination,
              document_text: this.selectedDocument.document_text,
              comments: this.parsedComments,
              layout_settings: this.selectedDocument.layout_settings
            }
          : { comments: this.parsedComments }
        const { data } = await api.put(`/generated-documents/${this.selectedDocument.id}`, payload)
        this.selectedDocument = this.normalizeDocument(data)
        this.renderEditorText()
        await this.loadDocuments()
        return this.selectedDocument
      } catch (error) {
        this.error = 'Não foi possível salvar o documento.'
        return null
      } finally {
        this.saving = false
      }
    },
    openAiAdjustDialog () {
      if (!this.selectedDocument || !this.canEditSelected) return
      this.syncEditorText()
      if (!String(this.selectedDocument.document_text || '').trim()) {
        this.error = 'Documento sem texto para ajustar.'
        return
      }
      this.aiAdjustError = ''
      this.aiAdjustSummary = ''
      this.aiAdjustForm = {
        mode: 'improve',
        instruction: ''
      }
      this.aiAdjustDialog = true
    },
    async adjustSelectedDocumentWithAi () {
      if (!this.selectedDocument || !this.canEditSelected) return
      this.syncEditorText()
      this.aiAdjusting = true
      this.aiAdjustError = ''
      this.aiAdjustSummary = ''
      this.error = ''

      try {
        const { data } = await api.post(`/generated-documents/${this.selectedDocument.id}/ai-adjust`, {
          document_text: this.selectedDocument.document_text,
          mode: this.aiAdjustForm.mode,
          instruction: this.aiAdjustForm.instruction
        })
        this.selectedDocument = this.normalizeDocument(data)
        this.documents = this.documents.map(item => item.id === this.selectedDocument.id ? this.selectedDocument : item)
        this.aiAdjustSummary = data.ai_adjustment && data.ai_adjustment.summary ? data.ai_adjustment.summary : 'Texto ajustado com IA.'
        this.successMessage = this.aiAdjustSummary
        this.aiAdjustDialog = false
        this.$nextTick(() => this.renderEditorText())
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : null
        this.aiAdjustError = data ? data.message : 'Não foi possível ajustar o documento.'
      } finally {
        this.aiAdjusting = false
      }
    },
    async validateDocument () {
      if (!this.selectedDocument) return
      if (!this.canEditSelected) {
        this.error = 'Você não tem permissão para validar este documento.'
        return
      }
      this.syncEditorText()
      this.requestValidationConfirmation(this.selectedDocument)
    },
    requestValidationConfirmation (document) {
      if (!document) return
      if (document.id === (this.selectedDocument && this.selectedDocument.id)) {
        this.syncEditorText()
        document = this.selectedDocument
      }
      const documentText = document.document_text || ''
      if (!this.canValidateDocument(document)) {
        this.error = 'Você não tem permissão para validar este documento.'
        return
      }
      if (!String(documentText || '').trim()) {
        this.error = 'Documento sem conteúdo para validar.'
        return
      }
      this.error = ''
      this.pendingValidationDocument = document
      this.validationConfirmDialog = true
    },
    async confirmValidation () {
      if (!this.pendingValidationDocument) return
      const document = this.pendingValidationDocument
      this.validationConfirmDialog = false
      await this.validateSavedDocument(document)
      this.pendingValidationDocument = null
    },
    async openPendingValidationDetails () {
      if (!this.pendingValidationDocument) return
      const document = this.pendingValidationDocument
      this.validationConfirmDialog = false

      if (this.selectedDocument && Number(this.selectedDocument.id) === Number(document.id)) {
        this.documentDialog = true
        return
      }

      try {
        await this.openDocument(document)
      } catch (error) {
        this.error = 'Não foi possível abrir os detalhes da validação.'
      }
    },
    async validateSavedDocument (document) {
      if (!document) return

      const documentText = document.document_text || (document.id === (this.selectedDocument && this.selectedDocument.id) ? this.selectedDocument.document_text : '')
      if (!this.canValidateDocument(document)) {
        this.error = 'Você não tem permissão para validar este documento.'
        return
      }
      if (!String(documentText || '').trim()) {
        this.error = 'Documento sem conteúdo para validar.'
        return
      }

      this.error = ''
      this.successMessage = ''
      this.validatingDocumentId = document.id

      try {
        const payload = new FormData()
        const intendedUse = document.destination || 'Uso interno'

        payload.append('document_type', document.document_type || 'Documento')
        payload.append('client_name', document.client_name || '')
        payload.append('matter_reference', document.matter_reference || '')
        payload.append('intended_use', intendedUse)
        payload.append('document_text', documentText)
        payload.append('human_reviewed', false)
        payload.append('source_checked', false)
        payload.append('client_data_present', Boolean(document.client_name || document.matter_reference))
        payload.append('external_delivery', !this.isInternalUse(intendedUse))

        const { data: validation } = await api.post('/ai-document-validations/validate', payload)
        const { data: updatedDocument } = await api.put(`/generated-documents/${document.id}`, {
          document_text: documentText,
          evidence_file_id: validation.evidence ? validation.evidence.id : document.evidence_file_id,
          validation_snapshot: validation
        })

        const normalizedDocument = this.normalizeDocument(updatedDocument)
        this.documents = this.documents.map(item => item.id === normalizedDocument.id ? normalizedDocument : item)
        if (this.selectedDocument && this.selectedDocument.id === normalizedDocument.id) {
          this.selectedDocument = normalizedDocument
          this.renderEditorText()
        }
        this.successMessage = 'Documento validado com os dados já cadastrados.'
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : null
        this.error = data ? data.message : 'Não foi possível validar este documento.'
      } finally {
        this.validatingDocumentId = null
      }
    },
    async openReviewDialog () {
      if (!this.selectedDocument || !this.canEditSelected || this.sendingToReview) return

      this.syncEditorText()
      this.error = ''
      this.reviewError = ''
      const documentText = String(this.selectedDocument.document_text || '').trim()
      if (documentText.length < 40) {
        this.error = documentText
          ? 'O documento precisa ter pelo menos 40 caracteres para revisão humana.'
          : 'Escreva o documento antes de enviar para revisão humana.'
        return
      }

      this.sendingToReview = true
      try {
        const savedDocument = await this.saveDocument()
        if (!savedDocument) return

        if (!this.employees.length && !this.reviewersLoading) {
          await this.loadReviewers()
        }

        this.reviewForm = this.prefillReviewForm(savedDocument)
        this.reviewForm.lastGeneratedRisk = this.reviewForm.quick_risk
        this.reviewForm.lastGeneratedBusinessPurpose = this.reviewForm.business_purpose
        this.reviewForm.lastGeneratedControls = this.reviewForm.controls
        this.reviewDialog = true
      } finally {
        this.sendingToReview = false
      }
    },
    prefillReviewForm (document) {
      const destination = String(document.destination || '').toLowerCase()
      const hasClientContext = Boolean(document.client_name || document.matter_reference)
      const quickRisk = /cliente|contraparte|protocol|processo|assinatura|extern/i.test(destination)
        ? 'external'
        : (hasClientContext ? 'client_data' : 'internal')
      const form = {
        ...this.emptyReviewForm(),
        confidentiality_level: hasClientContext ? 'confidential' : 'internal',
        human_reviewer: this.defaultReviewer(),
        quick_risk: quickRisk
      }
      return this.reviewRiskPayload(quickRisk, form)
    },
    reviewRiskPayload (risk, base = this.reviewForm) {
      const document = this.selectedDocument || {}
      const title = document.title || 'Documento'
      const type = document.document_type || 'Documento'
      const defaults = {
        internal: {
          quick_risk: 'internal',
          risk_level: 'low',
          confidentiality_level: 'internal',
          uses_personal_data: false,
          data_categories: 'Sem cliente, dado sensível ou envio externo identificado.',
          business_purpose: `${type} "${title}" precisa de revisão humana de consistência, clareza e aderência a políticas internas antes de seguir para uso interno.`,
          controls: 'Revisar clareza, consistência, placeholders, aderência a políticas internas e necessidade de ajustes antes de liberar.'
        },
        client_data: {
          quick_risk: 'client_data',
          risk_level: 'medium',
          confidentiality_level: 'confidential',
          uses_personal_data: true,
          data_categories: 'Documento, caso, contrato ou dado pessoal de cliente envolvido.',
          business_purpose: `${type} "${title}" precisa de decisão humana sobre dados de cliente, sigilo, valores, estratégia e contexto do caso.`,
          controls: 'Conferir dados de cliente/caso, sigilo, valores, estratégia, comentários internos, placeholders e informações sensíveis.'
        },
        external: {
          quick_risk: 'external',
          risk_level: 'high',
          confidentiality_level: 'confidential',
          uses_personal_data: true,
          data_categories: 'Documento será usado com cliente, contraparte, tribunal, assinatura ou terceiro.',
          business_purpose: `${type} "${title}" precisa de aprovação humana antes de saída externa para cliente, contraparte, assinatura, tribunal ou protocolo.`,
          controls: 'Validar conteúdo final, fundamentos, dados sensíveis, destinatário, anexos, assinatura e riscos antes de envio ou protocolo.'
        }
      }
      const nextDefaults = defaults[risk] || defaults.client_data
      const shouldReplaceBusinessPurpose = !base.business_purpose || base.business_purpose === base.lastGeneratedBusinessPurpose
      const shouldReplaceControls = !base.controls || base.controls === base.lastGeneratedControls
      const nextForm = {
        ...base,
        ...nextDefaults,
        business_purpose: shouldReplaceBusinessPurpose ? nextDefaults.business_purpose : base.business_purpose,
        controls: shouldReplaceControls ? nextDefaults.controls : base.controls
      }

      return {
        ...nextForm,
        lastGeneratedRisk: nextDefaults.quick_risk,
        lastGeneratedBusinessPurpose: nextDefaults.business_purpose,
        lastGeneratedControls: nextDefaults.controls
      }
    },
    applyReviewRisk (risk) {
      this.reviewForm = this.reviewRiskPayload(risk)
    },
    async loadReviewers () {
      this.reviewersLoading = true
      try {
        const { data } = await api.get('/employees')
        this.employees = data
      } catch (error) {
        this.employees = []
      } finally {
        this.reviewersLoading = false
      }
    },
    reviewerValue (employee) {
      const name = employee.name || employee.email
      return employee.email ? `${name} <${employee.email}>` : name
    },
    defaultReviewer () {
      const active = this.employees.filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
      const preferred = active.find(employee => ['legal', 'admin', 'tenant_owner', 'security_dpo'].includes(employee.role_slug))
      const reviewer = preferred || active[0]
      return reviewer ? this.reviewerValue(reviewer) : ''
    },
    async submitDocumentReview () {
      if (!this.selectedDocument || this.reviewSubmitting) return

      this.syncEditorText()
      this.reviewError = ''
      this.reviewSubmitting = true

      try {
        const payload = {
          tool_name: 'Documento para validação',
          vendor_name: 'ComplyOps',
          business_purpose: this.reviewForm.business_purpose || 'Documento enviado para revisão humana.',
          data_categories: this.reviewForm.data_categories || 'Riscos e dados sensíveis serão avaliados pelo responsável humano.',
          legal_basis: 'Políticas internas de validação documental',
          risk_level: this.reviewForm.risk_level || 'medium',
          uses_personal_data: Boolean(this.reviewForm.uses_personal_data),
          client_name: this.selectedDocument.client_name || '',
          matter_reference: this.selectedDocument.matter_reference || '',
          use_case_type: this.selectedDocument.document_type || 'Documento',
          confidentiality_level: this.reviewForm.confidentiality_level || 'internal',
          expected_benefit: this.reviewForm.expected_benefit || 'Liberar, ajustar ou bloquear documento antes do uso.',
          output_review_required: true,
          human_reviewer: this.reviewForm.human_reviewer || '',
          controls: this.reviewForm.controls || '',
          document_text: this.selectedDocument.document_text || '',
          submit_now: true
        }

        const formData = new FormData()
        Object.keys(payload).forEach(key => {
          if (payload[key] !== null && payload[key] !== undefined) {
            formData.append(key, payload[key])
          }
        })

        await api.post(`/generated-documents/${this.selectedDocument.id}/human-review-requests`, formData)
        this.selectedDocument = this.normalizeDocument({
          ...this.selectedDocument,
          status: 'in_review'
        })
        this.documents = this.documents.map(item => item.id === this.selectedDocument.id ? this.selectedDocument : item)
        this.reviewDialog = false
        await this.loadDocuments()
        this.successMessage = 'Documento enviado para revisão humana.'
      } catch (error) {
        this.reviewError = error.response && error.response.data
          ? error.response.data.message
          : (error.message || 'Não foi possível enviar o documento para revisão.')
      } finally {
        this.reviewSubmitting = false
      }
    },
    async downloadDocument (document, format = 'pdf') {
      if (!this.canDownloadDocument(document)) {
        this.error = 'Você não tem permissão para baixar este documento.'
        return
      }
      this.exportLoadingKey = this.exportKey(document, format)
      this.error = ''
      try {
        const { data } = await api.get(`/generated-documents/${document.id}/download`, {
          params: { format },
          responseType: 'blob'
        })
        const url = window.URL.createObjectURL(data)
        const link = window.document.createElement('a')
        link.href = url
        link.download = `${this.slug(document.title || 'documento')}.${format}`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.error = 'Não foi possível exportar este documento.'
      } finally {
        this.exportLoadingKey = ''
      }
    },
    exportKey (document, format) {
      return `${document && document.id ? document.id : 'document'}:${format}`
    },
    slug (value) {
      return String(value || 'documento')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'documento'
    },
    normalizeComments (comments) {
      if (Array.isArray(comments)) return comments
      if (!comments) return []
      try {
        return JSON.parse(comments) || []
      } catch (error) {
        return []
      }
    },
    normalizeJsonObject (value) {
      if (!value) return null
      if (typeof value === 'object') return value
      try {
        return JSON.parse(value)
      } catch (error) {
        return null
      }
    },
    normalizeDocument (document) {
      return {
        ...document,
        comments: this.normalizeComments(document.comments),
        validation_snapshot: this.normalizeJsonObject(document.validation_snapshot),
        layout_settings: this.normalizeLayoutSettings(document.layout_settings),
        access: document.access || { role: 'owner', permission: 'owner' }
      }
    },
    defaultLayoutSettings () {
      return {
        font_family: 'Georgia',
        font_size: 15,
        line_height: 1.65,
        text_align: 'left',
        text_color: '#17211f',
        highlight_color: '#fff0b3',
        cover: { enabled: false, title: '', subtitle: '', client_name: '', matter_reference: '', author: '', date: '' },
        header: { enabled: false, text: '' },
        footer: { enabled: false, text: '', show_page_number: false },
        watermark: { enabled: false, text: '', opacity: 0.08 },
        logo: { enabled: false, url: '' }
      }
    },
    normalizeLayoutSettings (settings) {
      const defaults = this.defaultLayoutSettings()
      let value = settings || {}
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value) || {}
        } catch (error) {
          value = {}
        }
      }
      return {
        ...defaults,
        ...value,
        cover: { ...defaults.cover, ...(value.cover || {}) },
        header: { ...defaults.header, ...(value.header || {}) },
        footer: { ...defaults.footer, ...(value.footer || {}) },
        watermark: { ...defaults.watermark, ...(value.watermark || {}) },
        logo: { ...defaults.logo, ...(value.logo || {}) }
      }
    },
    hasVisualLayout (layout) {
      const value = this.normalizeLayoutSettings(layout)
      return Boolean(
        (value.cover && value.cover.enabled) ||
        (value.header && value.header.enabled && value.header.text) ||
        (value.footer && value.footer.enabled && value.footer.text) ||
        (value.watermark && value.watermark.enabled && value.watermark.text) ||
        (value.logo && value.logo.enabled && value.logo.url)
      )
    },
    isSharedDocument (document) {
      return Boolean(document && document.access && document.access.role === 'shared')
    },
    shareOwnerLabel (document) {
      const owner = document && document.access ? document.access.owner : null
      return (owner && (owner.name || owner.email)) || 'outro workspace'
    },
    canShareDocument (document) {
      return !this.isSharedDocument(document)
    },
    canValidateDocument (document) {
      if (!document) return false
      return !this.isSharedDocument(document) || (document.access && document.access.permission === 'edit')
    },
    canDownloadDocument (document) {
      if (!document) return false
      return !this.isSharedDocument(document) || (document.access && document.access.permission === 'edit')
    },
    estimateValidationCredits (document) {
      if (!document) return 1
      const credits = this.estimateTextCredits(document.document_text || '')
      const inputCharacters = new Blob([String(document.document_text || '')]).size
      let adjustedCredits = credits

      const validation = this.validationDecision(document)
      const risk = validation && validation.risk_level ? validation.risk_level : ''
      const failedCount = this.validationFailedCount(document)
      const externalDelivery = !this.isInternalUse(document.destination || 'Uso interno')

      if (['high', 'critical'].includes(risk) && adjustedCredits < 2) adjustedCredits = 2
      if (risk === 'critical' && (externalDelivery || failedCount > 2) && adjustedCredits < 5) adjustedCredits = 5
      if (risk === 'critical' && externalDelivery && inputCharacters > 15000 && adjustedCredits < 10) adjustedCredits = 10
      if (risk === 'critical' && externalDelivery && inputCharacters > 40000) adjustedCredits = 30

      return adjustedCredits
    },
    estimateTextCredits (text) {
      const inputCharacters = new Blob([String(text || '')]).size
      if (inputCharacters > 60000) return 20
      if (inputCharacters > 40000) return 10
      if (inputCharacters > 15000) return 5
      if (inputCharacters > 5000) return 2
      return 1
    },
    permissionLabel (permission) {
      return {
        view: 'Visualizar',
        comment: 'Comentar',
        edit: 'Editar'
      }[permission] || 'Visualizar'
    },
    async acceptShareToken (token) {
      try {
        await api.post(`/document-shares/${token}/accept`)
        this.successMessage = 'Convite aceito. O documento apareceu em Compartilhados comigo.'
        this.documentScope = 'shared'
        this.$router.replace({ name: 'documents' })
      } catch (error) {
        this.error = 'Não foi possível aceitar este convite.'
      }
    },
    async openShareDialog (document) {
      this.shareTargetDocument = document
      this.shareDialog = true
      this.shareError = ''
      this.shareInviteUrl = ''
      this.shareForm = { email: '', permission: 'view' }
      await this.loadDocumentShares()
    },
    async loadDocumentShares () {
      if (!this.shareTargetDocument) return
      this.shareLoading = true
      try {
        const { data } = await api.get(`/generated-documents/${this.shareTargetDocument.id}/shares`)
        this.documentShares = data.shares || []
        this.shareSummary = data.summary || null
      } catch (error) {
        this.shareError = 'Não foi possível carregar compartilhamentos.'
      } finally {
        this.shareLoading = false
      }
    },
    async createShare () {
      if (!this.shareTargetDocument) return
      this.shareLoading = true
      this.shareError = ''
      this.shareInviteUrl = ''
      try {
        const { data } = await api.post(`/generated-documents/${this.shareTargetDocument.id}/shares`, this.shareForm)
        this.shareInviteUrl = data.invite_url || ''
        this.shareSummary = data.summary || this.shareSummary
        await this.loadDocumentShares()
        this.shareForm = { email: '', permission: 'view' }
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : null
        this.shareError = data ? data.message : 'Não foi possível compartilhar o documento.'
        if (data && data.summary) this.shareSummary = data.summary
      } finally {
        this.shareLoading = false
      }
    },
    async revokeShare (share) {
      if (!this.shareTargetDocument || !share) return
      this.shareLoading = true
      try {
        const { data } = await api.delete(`/generated-documents/${this.shareTargetDocument.id}/shares/${share.id}`)
        this.shareSummary = data.summary || this.shareSummary
        await this.loadDocumentShares()
      } catch (error) {
        this.shareError = 'Não foi possível revogar o compartilhamento.'
      } finally {
        this.shareLoading = false
      }
    },
    highlightDocumentText (text) {
      const value = String(text || '')
      if (!this.selectedValidation) return this.escapeHtml(value)

      const highlightBlocks = this.reviewHighlightBlocks(value)
      const highlightByIndex = highlightBlocks.reduce((acc, item) => {
        acc[item.index] = item
        return acc
      }, {})
      const blocks = value.split(/(\n{2,})/)
      return blocks.map((block, index) => {
        if (/^\n{2,}$/.test(block)) return this.escapeHtml(block)
        const highlighted = highlightByIndex[index]
        const marker = highlighted ? highlighted.marker : null
        const escaped = this.escapeHtml(block)
        if (!marker) return escaped
        return `<mark class="document-review-highlight document-review-highlight--${marker.type}" data-review-id="${highlighted.id}" title="${this.escapeHtml(marker.label)}">${escaped}</mark>`
      }).join('')
    },
    reviewHighlightBlocks (text) {
      const blocks = String(text || '').split(/(\n{2,})/)
      return blocks.reduce((acc, block, index) => {
        if (/^\n{2,}$/.test(block)) return acc
        const marker = this.blockReviewMarker(block)
        if (!marker) return acc
        acc.push({
          id: `review-marker-${index}`,
          index,
          text: block,
          marker
        })
        return acc
      }, [])
    },
    blockReviewMarker (block) {
      const aiPoint = this.matchAiAdjustmentPoint(block)
      if (aiPoint) {
        return {
          type: this.markerTypeFromAdjustment(aiPoint),
          label: aiPoint.issue || aiPoint.suggested_action || 'Ponto de ajuste identificado',
          issue: this.truncateReviewPreview(aiPoint.issue || aiPoint.suggested_action || aiPoint.excerpt)
        }
      }
      if (this.hasReviewMarker(block, 'numeric')) {
        return { type: 'numeric', label: 'Valor, período ou número suspeito para revisar' }
      }
      if (this.hasReviewMarker(block, 'placeholder')) {
        return { type: 'placeholder', label: 'Campo incompleto ou placeholder para revisar' }
      }
      if (this.hasReviewMarker(block, 'legal')) {
        return { type: 'legal', label: 'Referência jurídica para conferência em fonte oficial' }
      }
      if (this.hasReviewMarker(block, 'sensitive')) {
        return { type: 'sensitive', label: 'Dados sensíveis ou de cliente para revisar' }
      }
      return null
    },
    matchAiAdjustmentPoint (block) {
      const normalizedBlock = this.normalizeSearchText(block)
      return this.aiAdjustmentPoints.find(point => {
        const excerpt = this.normalizeSearchText(point.excerpt || '')
        if (excerpt && normalizedBlock.includes(excerpt.slice(0, Math.min(excerpt.length, 80)))) return true
        const issue = this.normalizeSearchText(point.issue || '')
        return issue && normalizedBlock.includes(issue)
      })
    },
    markerTypeFromAdjustment (point) {
      const category = this.normalizeSearchText(point.category || `${point.issue || ''} ${point.suggested_action || ''}`)
      if (category.includes('valor') || category.includes('period') || category.includes('data') || category.includes('numero')) return 'numeric'
      if (category.includes('fonte') || category.includes('jurid')) return 'legal'
      if (category.includes('dado') || category.includes('cliente')) return 'sensitive'
      if (category.includes('placeholder') || category.includes('incompleto')) return 'placeholder'
      return 'adjustment'
    },
    hasReviewMarker (text, type) {
      const raw = String(text || '')
      const normalized = this.normalizeSearchText(raw)
      const pending = this.pendingValidationText()

      if (type === 'numeric') {
        return (
          pending.includes('valor') ||
          pending.includes('period') ||
          pending.includes('prazo') ||
          pending.includes('numero') ||
          pending.includes('malformada') ||
          pending.includes('malformado')
        ) && (
          /R\$\s*[\d.,]*[a-zA-Z][\w.]*/i.test(raw) ||
          /\b(valor|pagamento|parcela|indenizacao)\b.{0,90}\b\d+[a-zA-Z]+\w*/i.test(normalized) ||
          /\b(periodo|prazo|mes|ano|vencimento)\b.{0,90}\b\d{6,}\b/i.test(normalized) ||
          /\b\d{5,}[a-zA-Z]{2,}\w*\b/i.test(raw)
        )
      }

      if (type === 'placeholder') {
        return (
          pending.includes('campo') ||
          pending.includes('placeholder') ||
          pending.includes('incompleto')
        ) && (
          /\[[^\]]+\]/.test(raw) ||
          /_{3,}/.test(raw) ||
          /\b0{3,}[-.\d]*\b/.test(raw) ||
          normalized.includes('preencher') ||
          normalized.includes('inserir')
        )
      }

      if (type === 'legal') {
        return (
          pending.includes('fonte') ||
          pending.includes('jurid') ||
          pending.includes('referencia')
        ) && /\b(artigo|art\.|lei|jurisprudencia|tribunal|sumula|fundamento legal|precedente)\b/.test(normalized)
      }

      if (type === 'sensitive') {
        return (
          pending.includes('dado') ||
          pending.includes('sensivel') ||
          pending.includes('cliente')
        ) && /\b(cpf|cnpj|cliente|contraparte|processo|valor|r\$|sigilo|confidencial|endereco)\b/.test(normalized)
      }

      return false
    },
    pendingValidationText () {
      if (!this.selectedValidation) return ''
      const checks = Array.isArray(this.selectedValidation.checks) ? this.selectedValidation.checks : []
      const failedChecks = checks.filter(check => check.status !== 'passed')
      const decision = this.selectedValidation.decision || {}
      const review = this.selectedValidation.ai_review && this.selectedValidation.ai_review.review
        ? this.selectedValidation.ai_review.review
        : {}
      const adjustmentText = Array.isArray(review.adjustment_points)
        ? review.adjustment_points.map(item => `${item.category || ''} ${item.issue || ''} ${item.suggested_action || ''}`).join(' ')
        : ''
      return this.normalizeSearchText([
        decision.message,
        decision.next_step,
        adjustmentText,
        ...failedChecks.map(check => `${check.name} ${check.message}`)
      ].join(' '))
    },
    normalizeSearchText (value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    truncateReviewPreview (value) {
      const text = String(value || '').replace(/\s+/g, ' ').trim()
      return text.length > 110 ? `${text.slice(0, 110)}...` : text
    },
    isInternalUse (value) {
      return String(value || '').toLowerCase().includes('interno')
    },
    validationSnapshot (document) {
      if (!document) return null
      return this.normalizeJsonObject(document.validation_snapshot)
    },
    validationDecision (document) {
      const snapshot = this.validationSnapshot(document)
      return snapshot && snapshot.decision ? snapshot.decision : null
    },
    validationDecisionLabel (document) {
      const decision = this.validationDecision(document)
      return decision ? this.validationDecisionDisplayLabel(decision) || this.statusLabel(document.status) : 'Sem validação'
    },
    validationDecisionDisplayLabel (decision) {
      if (!decision) return ''
      if (decision.status === 'needs_adjustment') return 'Requer ajustes'
      if (decision.status === 'approved_with_notes') return 'Com ressalvas'
      return decision.label || ''
    },
    validationRiskLabel (document) {
      const decision = this.validationDecision(document)
      return this.riskLabel(decision && decision.risk_level)
    },
    validationFailedCount (document) {
      const decision = this.validationDecision(document)
      return decision && decision.failed_count !== undefined ? decision.failed_count : 0
    },
    riskLabel (risk) {
      return { low: 'Baixo', medium: 'Médio', high: 'Alto', critical: 'Crítico' }[risk] || risk || '-'
    },
    statusLabel (status) {
      return {
        draft: 'Rascunho',
        validated: 'Validado',
        with_notes: 'Com ressalvas',
        blocked: 'Bloqueado',
        rejected: 'Bloqueado',
        approved: 'Aprovado',
        in_review: 'Em revisão',
        archived: 'Arquivado'
      }[status] || status || 'Rascunho'
    },
    reviewStatusLabel (status) {
      return {
        draft: 'Rascunho',
        submitted: 'Aguardando',
        in_review: 'Em revisão',
        approved: 'Aprovado',
        rejected: 'Bloqueado'
      }[status] || status || 'Rascunho'
    },
    reviewDecisionStep (review) {
      const approvals = Array.isArray(review.approvals) ? review.approvals : []
      return approvals
        .filter(step => ['approved', 'rejected'].includes(step.status))
        .sort((a, b) => new Date(b.decided_at || b.updated_at || 0) - new Date(a.decided_at || a.updated_at || 0))[0] || null
    },
    reviewDecisionReason (review) {
      const step = this.reviewDecisionStep(review)
      return (step && step.decision_reason) || review.review_checklist || review.controls || 'Sem motivo registrado.'
    },
    reviewDecisionActor (review) {
      const step = this.reviewDecisionStep(review)
      if (!step) return review.human_reviewer || 'Responsável não definido'

      const approver = step.approver || {}
      return approver.name || approver.email || review.human_reviewer || 'Responsável'
    },
    reviewDecisionDate (review) {
      const step = this.reviewDecisionStep(review)
      return this.formatDate((step && (step.decided_at || step.updated_at)) || review.completed_at || review.updated_at)
    },
    formatDate (value) {
      if (!value) return '-'
      if (String(value).startsWith('0000-00-00')) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    }
  }
}
</script>

<style lang="scss" scoped>
.page-heading-actions {
  display: flex;
  flex-wrap: nowrap;
  flex: 0 0 auto;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.documents-page .page-heading > div:first-child {
  min-width: 0;
}

.page-heading-actions .v-btn {
  height: 40px !important;
  min-width: 150px !important;
  padding: 0 14px !important;
  box-shadow: none !important;
}

.page-heading-actions .v-btn ::v-deep .v-btn__content {
  font-size: 13px;
  font-weight: 850;
  letter-spacing: 0;
  line-height: 1;
  white-space: nowrap;
}

.documents-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 18px;
}

.documents-toolbar__actions {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
}

.documents-toolbar h2 {
  margin: 0 0 4px;
}

.documents-toolbar span {
  display: block;
  color: #6b7280;
  font-size: 12px;
  margin-top: 3px;
}

.document-card-list {
  display: grid;
  gap: 12px;
}

.document-library-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 152px;
  gap: 18px;
  align-items: stretch;
  padding: 18px 20px;
  border: 1px solid #dde5e2;
  background: #ffffff;
}

.document-library-card__main {
  grid-column: 1;
  grid-row: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  min-width: 0;
}

.document-library-card__title {
  min-width: 0;
}

.document-library-card__title span,
.document-library-card__details span {
  display: block;
  color: #0f4efb;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: .08em;
  line-height: 1.2;
  text-transform: uppercase;
}

.document-library-card__title h3 {
  margin: 6px 0 0;
  color: #101918;
  font-size: 19px;
  line-height: 1.28;
  word-break: normal;
  overflow-wrap: break-word;
}

.document-library-card__title small {
  display: block;
  margin-top: 5px;
  color: #667085;
  font-size: 12px;
}

.document-library-card__details {
  grid-column: 1;
  grid-row: 2;
  display: grid;
  grid-template-columns: minmax(130px, 1fr) minmax(150px, 1.2fr) 64px 128px;
  gap: 12px;
  align-items: start;
  min-width: 0;
}

.document-library-card__details section {
  min-width: 0;
}

.document-library-card__details strong,
.document-library-card__details p {
  display: block;
  margin: 6px 0 0;
  color: #101918;
  font-size: 13px;
  line-height: 1.35;
}

.document-library-card__details p,
.document-library-card__details small {
  color: #667085;
}

.document-library-card__actions {
  grid-column: 2;
  grid-row: 1 / span 2;
  display: grid;
  grid-template-columns: 152px;
  gap: 6px;
  align-content: center;
  justify-content: end;
  min-width: 0;
}

.document-library-card__actions .v-btn {
  width: 100%;
  height: 32px !important;
  min-width: 0 !important;
  padding: 0 10px !important;
  border-color: rgba(15, 78, 251, .42) !important;
  background: #ffffff !important;
  box-shadow: none !important;
  color: #0f4efb !important;
  justify-content: flex-start;
}

.document-library-card__actions .v-btn ::v-deep .v-btn__content {
  min-width: 0;
  width: 100%;
  justify-content: flex-start;
  gap: 6px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-library-card__actions .v-btn ::v-deep .v-icon {
  margin-right: 2px !important;
}

.document-library-card__actions .document-action-button--primary {
  border-color: rgba(15, 78, 251, .55) !important;
  background: #f4f7ff !important;
}

.empty-document-list {
  padding: 32px;
  border: 1px dashed #dde5e2;
  text-align: center;
  color: #6b7280;
}

.documents-pagination {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #dde5e2;
}

.documents-pagination span {
  color: #667085;
  font-size: 13px;
  font-weight: 700;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  background: #e9edf2;
  color: #172033;
  font-size: 11px;
  font-weight: 800;
}

.status-pill--validated {
  background: #dff7ea;
  color: #0f6b3e;
}

.status-pill--with_notes,
.status-pill--in_review {
  background: #fff1d6;
  color: #8a5a00;
}

.status-pill--blocked,
.status-pill--rejected {
  background: #f7d7dc;
  color: #9f2139;
}

.status-pill--approved {
  background: #dff7ea;
  color: #0f6b3e;
}

.validation-list-summary strong {
  display: block;
  color: #101623;
  font-size: 14px;
  line-height: 1.25;
}

.validation-list-summary small {
  color: #667085;
}

.document-workspace {
  min-height: 100vh;
  background: #fafaf8 !important;
}

.document-workspace-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 68px;
  padding: 10px 18px;
  border-bottom: 1px solid #dde5e2;
  background: #ffffff;
}

.document-workspace-title {
  min-width: 0;
}

.document-workspace-title span {
  color: #0f4efb;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.document-workspace-header h2 {
  max-width: min(720px, 48vw);
  margin: 0;
  color: #101918;
  font-size: 21px;
  line-height: 1.18;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-workspace-header p {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.25;
}

.document-workspace-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
}

.document-workspace-actions .v-btn {
  height: 30px !important;
  min-width: 0 !important;
  box-shadow: none !important;
}

.document-workspace-action {
  min-width: 104px !important;
  padding: 0 11px !important;
  border-color: rgba(15, 78, 251, .45) !important;
  background: #ffffff !important;
  color: #0f4efb !important;
}

.document-workspace-action--primary {
  border-color: rgba(15, 78, 251, .62) !important;
  background: #f4f7ff !important;
}

.document-workspace-actions .v-btn ::v-deep .v-btn__content {
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0;
  line-height: 1;
  white-space: nowrap;
}

.document-workspace-close {
  width: 30px !important;
  min-width: 30px !important;
  color: #586576 !important;
  padding: 0 !important;
}

.document-workspace-close:hover {
  background: #f3f6f8 !important;
}

.document-workspace-feedback {
  padding: 10px 18px 0;
  background: #fafaf8;
}

.document-workspace-feedback .v-alert {
  margin: 0;
}

.shared-document-note {
  margin-top: 18px;
  padding: 10px 12px;
  border: 1px solid #cfe0ff;
  background: #f3f7ff;
  color: #1746b8;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.45;
}

.document-share-dialog .v-card__title {
  font-weight: 850;
}

.share-limit-summary {
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #dde5e2;
  background: #f8fafc;
  color: #45524f;
  font-size: 13px;
  font-weight: 750;
}

.share-form-grid {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 160px auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 18px;
}

.share-list {
  display: grid;
  gap: 8px;
}

.share-list article {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #dde5e2;
  background: #ffffff;
}

.share-list strong,
.share-list span {
  display: block;
}

.share-list span {
  margin-top: 3px;
  color: #667085;
  font-size: 12px;
}

.validation-confirm-dialog .v-card__title {
  padding-bottom: 6px;
  font-size: 18px;
  font-weight: 850;
}

.validation-confirm-dialog p {
  margin: 0 0 12px;
  color: #45524f;
  font-size: 13px;
  line-height: 1.45;
}

.validation-existing-preview {
  display: grid;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #cfe0ff;
  border-left: 3px solid #0f4efb;
  background: #f7faff;
}

.validation-existing-preview__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.validation-existing-preview__header span:first-child {
  display: block;
  color: #0f4efb;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.validation-existing-preview__header strong {
  display: block;
  margin-top: 4px;
  color: #101918;
  font-size: 12px;
  line-height: 1.25;
}

.validation-confirm-dialog .validation-existing-preview p {
  margin: 0;
  color: #344054;
}

.validation-existing-preview button {
  justify-self: start;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0f4efb;
  font-size: 12px;
  font-weight: 850;
  cursor: pointer;
}

.validation-existing-preview button:hover {
  text-decoration: underline;
}

.validation-credit-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #d8e2ff;
  background: #f6f9ff;
}

.validation-credit-preview span {
  color: #60717d;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.validation-credit-preview strong {
  color: #0f4efb;
  font-size: 14px;
  font-weight: 850;
}

.manual-document-dialog .v-card__title,
.ai-adjust-dialog .v-card__title,
.document-review-dialog .v-card__title {
  font-size: 18px;
  font-weight: 850;
}

.document-review-dialog .v-card__title span {
  display: block;
  color: #0f4efb;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  line-height: 1;
  text-transform: uppercase;
}

.document-review-dialog .v-card__title strong {
  display: block;
  margin-top: 6px;
  color: #101918;
  font-size: 20px;
  line-height: 1.2;
}

.review-document-summary {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}

.review-document-summary div {
  min-width: 0;
  padding: 12px;
  border: 1px solid #dde5e2;
  background: #fbfcfc;
}

.review-document-summary span {
  display: block;
  color: #60717d;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.review-document-summary strong {
  display: block;
  margin-top: 6px;
  color: #101918;
  font-size: 13px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-mode-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.review-mode-grid button {
  min-height: 116px;
  padding: 13px;
  border: 1px solid #dde5e2;
  background: #ffffff;
  color: #101918;
  cursor: pointer;
  text-align: left;
}

.review-mode-grid button.active,
.review-mode-grid button:hover {
  border-color: #0f4efb;
  background: #f4f7ff;
}

.review-mode-grid .v-icon {
  color: #0f4efb;
}

.review-mode-grid strong,
.review-mode-grid span {
  display: block;
}

.review-mode-grid strong {
  margin-top: 9px;
  font-size: 13px;
  line-height: 1.25;
}

.review-mode-grid span {
  margin-top: 5px;
  color: #60717d;
  font-size: 12px;
  line-height: 1.35;
}

.review-dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 12px 0;
}

.manual-document-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
  padding-top: 8px;
}

.manual-document-grid > :last-child {
  grid-column: 1 / -1;
}

.ai-adjust-mode-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.ai-adjust-mode-grid button {
  min-height: 104px;
  padding: 12px;
  border: 1px solid #dde5e2;
  background: #ffffff;
  color: #101918;
  cursor: pointer;
  text-align: left;
}

.ai-adjust-mode-grid button.active,
.ai-adjust-mode-grid button:hover {
  border-color: #0f4efb;
  background: #f4f7ff;
}

.ai-adjust-mode-grid .v-icon {
  color: #0f4efb;
}

.ai-adjust-mode-grid strong,
.ai-adjust-mode-grid span {
  display: block;
}

.ai-adjust-mode-grid strong {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.25;
}

.ai-adjust-mode-grid span {
  margin-top: 4px;
  color: #60717d;
  font-size: 12px;
  line-height: 1.35;
}

.ai-adjust-credit-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid #d8e2ff;
  background: #f6f9ff;
}

.ai-adjust-credit-preview span {
  color: #60717d;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.ai-adjust-credit-preview strong {
  color: #0f4efb;
  font-size: 14px;
  font-weight: 850;
}

.document-workspace-body {
  display: grid;
  grid-template-columns: 320px minmax(520px, 1fr);
  gap: 14px;
  height: calc(100vh - 96px);
  padding: 14px;
}

.document-meta-panel,
.document-comments-panel,
.document-edit-viewer {
  border: 1px solid #dde5e2;
  background: #ffffff;
  overflow: auto;
}

.document-meta-panel,
.document-comments-panel {
  padding: 16px;
}

.document-meta-panel h3,
.document-comments-panel h3 {
  margin: 0 0 16px;
}

.document-meta-panel label {
  display: block;
  margin: 14px 0 6px;
  color: #667085;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.document-review-history {
  margin-top: 20px;
}

.document-side-section {
  padding: 14px;
  border: 1px solid #dde5e2;
  background: #fbfcfc;
}

.document-side-section--human {
  border-left: 3px solid #101918;
}

.document-side-section--ai {
  margin-top: 18px;
  border-color: #cfe0ff;
  border-left: 3px solid #0f4efb;
  background: #f7faff;
}

.document-side-section__title {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 9px;
}

.document-side-section__icon {
  display: inline-grid !important;
  place-items: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border: 1px solid #d8e1e7;
  background: #ffffff;
  color: #0f4efb;
}

.document-side-section--human .document-side-section__icon {
  color: #101918;
}

.document-side-section__title > div > span {
  display: block;
  color: #0f4efb;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .08em;
  line-height: 1;
  text-transform: uppercase;
}

.document-side-section--human .document-side-section__title > div > span {
  color: #101918;
}

.document-side-section__description {
  margin: 0 0 12px;
  color: #667085;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.45;
}

.document-review-history__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.document-meta-panel .document-review-history__header h3 {
  margin: 2px 0 0;
  color: #101918;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.25;
}

.document-review-history__header span {
  display: block;
  color: #0f4efb;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .08em;
  line-height: 1;
  text-transform: uppercase;
}

.document-review-history__header strong {
  display: inline-grid;
  place-items: center;
  min-width: 28px;
  height: 28px;
  border: 1px solid #cfd8de;
  background: #ffffff;
  color: #101918;
  font-size: 13px;
  font-weight: 900;
}

.document-review-history__list {
  display: grid;
  gap: 10px;
}

.document-review-history__item {
  display: grid;
  gap: 9px;
  padding: 12px;
  border: 1px solid #e3e9ed;
  border-left: 3px solid #0f4efb;
  background: #ffffff;
}

.document-review-history__item:has(.status-pill--rejected) {
  border-left-color: #b4233b;
}

.document-review-history__item:has(.status-pill--approved) {
  border-left-color: #138a52;
}

.document-review-history__item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.document-review-history__item small {
  color: #647280;
  font-size: 11px;
  line-height: 1.25;
  text-align: right;
}

.document-review-history__actor span,
.document-review-history__reason span {
  display: block;
  margin-bottom: 3px;
  color: #667085;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.document-review-history__actor strong {
  color: #101918;
  font-size: 13px;
  font-weight: 850;
  line-height: 1.3;
}

.document-review-history__reason {
  padding-top: 8px;
  border-top: 1px solid #edf1f3;
}

.document-review-history__reason p,
.document-review-history__empty {
  margin: 0;
  color: #647280;
  font-size: 12px;
  line-height: 1.45;
}

.document-validation-card__header {
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: 10px;
  margin-bottom: 12px;
}

.document-meta-panel .document-validation-card__header h3 {
  margin: 0;
  max-width: none;
  color: #101918;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.25;
}

.document-validation-card__header .validation-decision-pill {
  justify-self: start;
  margin-left: 39px;
  max-width: 100%;
}

.validation-message {
  margin: 0 0 6px;
  color: #101623;
  font-size: 13px;
  font-weight: 750;
  line-height: 1.45;
}

.validation-next-step {
  display: block;
  padding: 10px 12px;
  border: 1px solid #dde5e2;
  background: #f8fafc;
  color: #667085;
  font-size: 12px;
  line-height: 1.45;
}

.validation-decision-pill {
  flex: 0 0 auto;
  max-width: 130px;
  min-height: 24px;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: #e9edf2;
  color: #172033;
  font-size: 9px;
  font-weight: 850;
  letter-spacing: .04em;
  line-height: 1.15;
  text-align: center;
  text-transform: uppercase;
}

.validation-decision-pill--approved {
  border-color: #a8dec0;
  background: #e9f8ef;
  color: #0f6b3e;
}

.validation-decision-pill--approved_with_notes,
.validation-decision-pill--needs_adjustment {
  border-color: #ead19b;
  background: #fff7e5;
  color: #8a5a00;
}

.validation-decision-pill--blocked {
  border-color: #efb5c0;
  background: #fff0f3;
  color: #9f2139;
}

.validation-mini-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 14px 0;
}

.validation-mini-grid article:last-child:nth-child(odd) {
  grid-column: 1 / -1;
}

.validation-mini-grid article {
  min-width: 0;
  padding: 10px 8px;
  border: 1px solid #dde5e2;
  background: #f8fafc;
}

.validation-mini-grid span {
  display: block;
  color: #667085;
  font-size: 9px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}

.validation-mini-grid strong {
  display: block;
  margin-top: 4px;
  color: #101623;
  font-size: 13px;
  line-height: 1.2;
}

.validation-ai-summary,
.validation-adjustment-list,
.validation-pending-checks {
  padding: 12px;
  border: 1px solid #dde5e2;
  background: #ffffff;
}

.validation-ai-summary + .validation-adjustment-list,
.validation-ai-summary + .validation-pending-checks,
.validation-adjustment-list + .validation-pending-checks {
  margin-top: 10px;
}

.validation-ai-summary strong,
.validation-adjustment-list strong,
.validation-pending-checks strong {
  display: block;
  margin-bottom: 6px;
  color: #101623;
  font-size: 12px;
}

.validation-ai-summary p {
  margin: 0;
  color: #394452;
  font-size: 12px;
  font-weight: 500;
}

.validation-adjustment-list button {
  display: block;
  width: 100%;
  padding: 9px 10px;
  border: 1px solid #dde5e2;
  background: #f8fafc;
  color: #101623;
  text-align: left;
  cursor: pointer;
}

.validation-adjustment-list button + button {
  margin-top: 8px;
}

.validation-adjustment-list button.active,
.validation-adjustment-list button:hover {
  border-color: #0f4efb;
  background: #eef5ff;
}

.validation-adjustment-list span {
  display: block;
  color: #0f4efb;
  font-size: 11px;
  font-weight: 850;
}

.validation-adjustment-list p {
  margin: 4px 0 0;
  color: #394452;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.validation-pending-checks ul {
  margin: 0;
  padding-left: 16px;
  color: #394452;
  font-size: 12px;
}

@media (max-width: 1180px) {
  .document-library-card {
    grid-template-columns: 1fr;
  }

  .document-library-card__main {
    grid-template-columns: 1fr;
  }

  .document-library-card__details {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .document-library-card__actions {
    grid-column: 1;
    grid-row: auto;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-content: start;
    justify-content: stretch;
  }

  .documents-pagination {
    align-items: flex-start;
    flex-direction: column;
  }

  .document-workspace-body {
    grid-template-columns: 1fr;
    height: auto;
  }

  .document-workspace-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .document-workspace-header h2 {
    max-width: 100%;
  }

  .document-workspace-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 700px) {
  .manual-document-grid,
  .ai-adjust-mode-grid,
  .review-document-summary,
  .review-mode-grid,
  .review-dialog-grid {
    grid-template-columns: 1fr;
  }

  .page-heading-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
    width: 100%;
  }

  .page-heading-actions .v-btn {
    flex: 1 1 100%;
  }

  .document-library-card {
    grid-template-columns: 1fr;
  }

  .document-library-card__main,
  .document-library-card__details {
    grid-column: auto;
    grid-row: auto;
    grid-template-columns: 1fr;
  }

  .document-library-card__actions {
    grid-column: auto;
    grid-row: auto;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
